import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { store } from './store';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <App />
    </Provider>
  </React.StrictMode>
);

// Register the production service worker. No-op in development, so the dev
// server behaves exactly as before.
serviceWorkerRegistration.register({
  onUpdate(registration) {
    // A newer build was precached. Ask it to skip waiting so it activates
    // immediately; the update check below then reloads onto the new build.
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  },
});

// Self-updating check: compare the main bundle referenced by the served
// index.html with the one currently running. If a newer build is deployed,
// reload — but only in the background or after the visitor goes idle, so it
// never interrupts someone who is reading the page.
const runningBundle = (() => {
  const src = Array.from(document.querySelectorAll('script[src]'))
    .map((s) => s.getAttribute('src'))
    .find((s) => s && /main\.[a-f0-9]+\.js/.test(s));
  return src ? src.split('/').pop() : null;
})();

let reloadArmed = false;

const scheduleReload = (served) => {
  if (reloadArmed || sessionStorage.getItem('avd_reloaded_to') === served) return;
  reloadArmed = true;

  const doReload = () => {
    if (sessionStorage.getItem('avd_reloaded_to') === served) return;
    sessionStorage.setItem('avd_reloaded_to', served);
    window.location.reload();
  };

  // The tab is already in the background — reload straight away.
  if (document.visibilityState === 'hidden') {
    doReload();
    return;
  }

  // Idle events reset the timer, so an active viewer shifts the reload out
  // until they stop interacting or switch tabs.
  const IDLE_MS = 90 * 1000;
  const MAX_MS = 5 * 60 * 1000;
  let idleTimer = null;

  const cleanup = () => {
    window.removeEventListener('visibilitychange', onHidden);
    ['mousemove', 'keydown', 'scroll', 'touchstart', 'pointerdown'].forEach((event) =>
      window.removeEventListener(event, resetIdle)
    );
    if (idleTimer) clearTimeout(idleTimer);
  };

  const resetIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(doReload, IDLE_MS);
  };

  const onHidden = () => {
    if (document.visibilityState !== 'hidden') return;
    cleanup();
    doReload();
  };

  window.addEventListener('visibilitychange', onHidden);
  ['mousemove', 'keydown', 'scroll', 'touchstart', 'pointerdown'].forEach((event) =>
    window.addEventListener(event, resetIdle, { passive: true })
  );
  resetIdle();
  window.setTimeout(() => {
    cleanup();
    doReload();
  }, MAX_MS);
};

const checkForUpdate = () => {
  if (!runningBundle || typeof fetch !== 'function') return;
  fetch(`${process.env.PUBLIC_URL || ''}/`, { cache: 'no-store' })
    .then((res) => res.text())
    .then((html) => {
      const match = html.match(/main\.[a-f0-9]+\.js/);
      if (!match) return;
      const served = match[0];
      // Reload whenever a newer build is served. Track the bundle we last
      // reloaded to so repeated deploys each get picked up, while avoiding
      // any reload loop.
      if (served !== runningBundle) {
        scheduleReload(served);
      }
    })
    .catch(() => {});
};

window.addEventListener('load', () => {
  window.setTimeout(checkForUpdate, 1500);
  window.setInterval(checkForUpdate, 120000);
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
